// Shared date/relative-time formatters. Consolidates several hand-rolled
// copies that accumulated independently across the main client, apps/admin,
// and other app bundles — each export below pins the EXACT output and
// thresholds of the specific duplicated implementation it replaces (see the
// per-export comments). This is a pure consolidation, not a redesign: do not
// "fix" pluralization, merge formats, or add guards to a variant that never
// had one — that would change behavior at call sites that depend on it.
//
// Browser-safe (no DOM access at module top level) so it can be imported by
// both client bundles and, if ever needed, the server.

/**
 * Relative "time ago" string, matching the semantics of the former
 * `timeAgo()` in src/client/modules/leaderboards.js. `dateLike` must be a
 * Date (the original only ever received `new Date(...)`). `nowMs` defaults
 * to `Date.now()` and exists so callers/tests can pin a fixed instant.
 *
 * Buckets: <60s "just now", <1h "Xm ago", <24h "Xh ago", <30d "Xd ago",
 * else falls back to `date.toLocaleDateString()`. All bucket counts are
 * floored (Math.floor), not rounded.
 */
export function timeAgo(dateLike, nowMs = Date.now()) {
  const date = dateLike instanceof Date ? dateLike : new Date(dateLike);
  const ms = nowMs - date.getTime();
  if (ms < 60_000) return 'just now';
  if (ms < 3_600_000) return `${Math.floor(ms / 60_000)}m ago`;
  if (ms < 86_400_000) return `${Math.floor(ms / 3_600_000)}h ago`;
  if (ms < 2_592_000_000) return `${Math.floor(ms / 86_400_000)}d ago`;
  return date.toLocaleDateString();
}

/**
 * Short date, e.g. "Mar 7" (no year). Matches the former `formatShortDate()`
 * duplicated in apps/admin/src/screens/dashboard.js, apps/admin/src/screens/
 * users.js, src/client/modules/stats.js, and src/client/main.js
 * (`_formatShortDate`, unused/dead in main.js but preserved verbatim).
 * Falsy input or a throwing Date conversion both yield an em dash '—'.
 */
export function formatShortDate(ts) {
  if (!ts) return '–';
  try {
    return new Date(ts).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  } catch {
    return '–';
  }
}

/**
 * Date only, e.g. "Mar 7, 2026". Matches the former `fmtDate()` in
 * src/client/news-modal.js. Falsy input yields an empty string (NOT an em
 * dash — distinct from formatShortDate's fallback, preserved as-is).
 */
export function formatDate(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

/**
 * Date + time, e.g. "Mar 7, 2026 03:30 PM". Matches the former `fmtDate()`
 * duplicated (byte-identical) in apps/admin/src/screens/contact.js,
 * moderation.js, news.js, and reports.js. Deliberately has NO falsy-input
 * guard, matching all four originals: `new Date(undefined)` produces an
 * Invalid Date, which renders as the literal string "Invalid Date" for both
 * the date and time halves.
 */
export function formatDateTime(iso) {
  const d = new Date(iso);
  return d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' })
    + ' ' + d.toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

/**
 * Humanized cumulative playtime from a SECONDS count. Matches the former
 * `formatPlayTime()` in src/client/modules/stats.js exactly: `<1m` under a
 * minute (incl. 0/falsy), `Xm` under an hour, else `Xh Ym` with the minutes
 * dropped when zero (`Xh`). All buckets floor. Used by the homepage compete
 * standings and the /leaderboards global playtime board.
 */
export function formatPlayTime(seconds) {
  if (!seconds || seconds < 60) return '<1m';
  const totalMin = Math.floor(seconds / 60);
  if (totalMin < 60) return `${totalMin}m`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return m ? `${h}h ${m}m` : `${h}h`;
}
